import React, { useEffect, useRef, useState } from 'react';
import PropertyGallery from './PropertyGallery';
import PropertyBookingCard from './PropertyBookingCard';
import { Star, MapPin, Bed, Bath, Users, Wifi, ShieldCheck, Waves, ChevronDown, ChevronUp } from 'lucide-react';
const API_BASE = window.__APP_API_BASE__ || process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Reutilizamos el mismo cargador singleton del sistema
// gm_authFailure ya es manejado globalmente en AgregarPropiedades.js con CustomEvent.
// Si solo se carga PropertyDetailView sin AgregarPropiedades, definimos el handler aquí también.
if (!window.__gmAuthFailureSet) {
    window.__gmAuthFailureSet = true;
    window.gm_authFailure = () => window.dispatchEvent(new CustomEvent('gm-auth-failure'));
}

let clientMapsLoaded = false;
const loadClientMaps = (apiKey) => new Promise((resolve, reject) => {
    if (window.google?.maps) { resolve(window.google.maps); return; }
    if (clientMapsLoaded) { const t = setInterval(() => { if (window.google?.maps) { clearInterval(t); resolve(window.google.maps); } }, 100); return; }
    clientMapsLoaded = true;
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&language=es&region=CO`;
    s.async = true; s.defer = true;
    s.onload = () => resolve(window.google.maps);
    s.onerror = () => { clientMapsLoaded = false; reject(new Error('Script de Google Maps no pudo cargarse')); };
    document.head.appendChild(s);
});

// Mini mapa estático para el cliente (solo lectura, sin library de Places)
const PropertyMap = ({ lat, lng, address, apiKey }) => {
    const divRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const [mapErr, setMapErr] = useState(null);

    useEffect(() => {
        if (!divRef.current || !apiKey || !lat || !lng) return;
        setMapErr(null);
        loadClientMaps(apiKey).then((maps) => {
            const map = new maps.Map(divRef.current, {
                center: { lat, lng },
                zoom: 16,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
                zoomControl: true,
                gestureHandling: 'cooperative',
                styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }]
            });
            new maps.Marker({ position: { lat, lng }, map, title: address || 'Propiedad' });
            setLoaded(true);
        }).catch(err => {
            console.error('[Maps] Error:', err);
            setMapErr('No se pudo cargar el mapa. Verifica que la API key tenga Maps JavaScript API habilitada.');
        });
    }, [lat, lng, apiKey, address]);

    if (!apiKey || !lat || !lng) return null;

    return (
        <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', height: '360px', marginTop: '8px', border: '1px solid #eee' }}>
            <div ref={divRef} style={{ width: '100%', height: '100%' }} />
            {!loaded && !mapErr && (
                <div style={{ position: 'absolute', inset: 0, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ width: '36px', height: '36px', border: '4px solid #003366', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
                    <p style={{ fontSize: '13px', color: '#64748b' }}>Cargando mapa...</p>
                </div>
            )}
            {mapErr && (
                <div style={{ position: 'absolute', inset: 0, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px', padding: '24px', textAlign: 'center' }}>
                    <span style={{ fontSize: '32px' }}>🗺️</span>
                    <p style={{ fontSize: '13px', fontWeight: 700, color: '#dc2626' }}>Error al cargar el mapa</p>
                    <p style={{ fontSize: '11px', color: '#b91c1c' }}>{mapErr}</p>
                </div>
            )}
        </div>
    );
};

const PropertyDetailView = ({ property, auth, settings }) => {
  const [mapsApiKey, setMapsApiKey] = useState('');
  const [roomsOpen, setRoomsOpen] = useState(false);
  const [reviews, setReviews] = useState({ promedio: 0, total: 0 });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API_BASE}/api/public/maps-key`)
      .then(r => r.json())
      .then(d => setMapsApiKey(d?.key || ''))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!property?.id) return;
    fetch(`${API_BASE}/api/reviews/property/${property.id}`)
      .then(r => r.json())
      .then(d => setReviews({ promedio: d.promedio || 0, total: d.total || 0 }))
      .catch(() => {});
  }, [property?.id]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  // Detalle estructurado de habitaciones (guardado por el admin en property.beds)
  let roomsConfig = [];
  try {
    const bedsParsed = property?.beds ? JSON.parse(property.beds) : null;
    if (Array.isArray(bedsParsed?.roomsConfig)) roomsConfig = bedsParsed.roomsConfig;
  } catch (e) { /* beds no es JSON válido */ }

  if (!property) return (
    <div style={loaderContainer}>
      <Waves size={40} color="#003366" className="animate-pulse" />
      <p style={{marginTop: '15px', fontWeight: '600'}}>Cargando tu refugio en Cartagena...</p>
    </div>
  );

  return (
    <div style={pageLayout} className="property-detail-page">
      {/* 1. TÍTULO Y UBICACIÓN */}
      <div style={headerSection}>
        <h1 style={mainTitle}>{property.title || property.nickname}</h1>
        <div style={subHeader} className="property-subheader">
          <div style={infoGroup} className="property-info-group">
            <div style={ratingBadge}>
              <Star size={14} fill="#DAA520" color="#DAA520" />
              <span style={{fontWeight: '800'}}>{reviews.total > 0 ? reviews.promedio.toFixed(1) : '5.0'}</span>
              <span style={{color: '#717171'}}> · {reviews.total > 0 ? `${reviews.total} reseña${reviews.total !== 1 ? 's' : ''}` : 'Nueva Propiedad'}</span>
            </div>
            <span style={dotDivider}>•</span>
            <span style={locationText} className="property-location-text">
              <MapPin size={14} /> {property.address || "Cartagena, Colombia"}
            </span>
          </div>

          <div style={shareActions}>
            <button style={textActionBtn} onClick={handleShare}>{copied ? '¡Enlace copiado!' : 'Compartir'}</button>
          </div>
        </div>
      </div>

      {/* 2. GALERÍA DE FOTOS (Se pasa la propiedad completa para que PropertyGallery use el baseUrl) */}
      <PropertyGallery property={property} />

      {/* 3. CONTENIDO DIVIDIDO */}
      <div style={mainContentGrid} className="property-content-grid">

        {/* COLUMNA IZQUIERDA: INFORMACIÓN */}
        <div style={infoColumn} className="property-info-column">
          <div style={hostSection}>
            <div>
              <div style={specList}>
                <span>{property.guests || 0} huéspedes</span> • 
                <span>{property.bedrooms || 0} habitaciones</span> • 
                <span>{property.fullBathrooms || 0} baños</span>
              </div>
            </div>
          </div>

          <hr style={divider} />

          {/* BENEFICIOS DESTACADOS */}
          <div style={highlightSection}>
            <div style={highlightRow}>
              <ShieldCheck size={24} color="#003366" />
              <div>
                <div style={highlightTitle}>Reserva Protegida</div>
                <div style={highlightSub}>Confirmación inmediata y gestión directa con el propietario.</div>
              </div>
            </div>
            <div style={highlightRow}>
              <Wifi size={24} color="#003366" />
              <div>
                <div style={highlightTitle}>Wifi de alta velocidad</div>
                <div style={highlightSub}>Conexión estable, ideal para trabajo remoto.</div>
              </div>
            </div>
          </div>

          <hr style={divider} />

          {/* DESCRIPCIÓN */}
          <div style={descriptionSection}>
            <h3 style={smallTitle}>Sobre este espacio</h3>
            <p style={descriptionText}>
              {property.description || "Disfruta de la brisa marina en este exclusivo espacio completamente equipado en el corazón de Cartagena."}
            </p>
          </div>

          {roomsConfig.length > 0 && (
            <>
              <hr style={divider} />
              <div style={descriptionSection}>
                <button type="button" onClick={() => setRoomsOpen(o => !o)} style={roomsToggleBtn}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Bed size={20} color="#003366" /> Habitaciones ({roomsConfig.length})
                  </span>
                  {roomsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>

                {roomsOpen && (
                  <div style={roomsScrollRow}>
                    {roomsConfig.map((room, i) => {
                      const camas = Object.entries(room.camas || {}).filter(([, cant]) => cant > 0);
                      const extras = [...(room.extras || []), ...(room.customExtras || [])];
                      return (
                        <div key={i} style={roomCard}>
                          <p style={roomCardTitle}>Habitación {i + 1}</p>
                          {camas.map(([tipo, cant]) => (
                            <div key={tipo} style={roomCardRow}><Bed size={14} /> {cant} × {tipo}</div>
                          ))}
                          <div style={roomCardRow}>
                            <Bath size={14} /> Baño {room.bano === 'privado' ? 'privado' : 'compartido'}
                          </div>
                          {extras.length > 0 && (
                            <div style={roomChipsRow}>
                              {extras.map(ex => <span key={ex} style={roomChip}>{ex}</span>)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          <hr style={divider} />

          {/* AMENIDADES */}
          <h3 style={smallTitle}>Lo que ofrece este lugar</h3>
          <div style={amenitiesGrid}>
            <div style={amenityItem}><Wifi size={20} /> Wifi</div>
            <div style={amenityItem}><Users size={20} /> Ascensor</div>
            <div style={amenityItem}><Bed size={20} /> Sábanas de algodón</div>
            <div style={amenityItem}><Bath size={20} /> Agua caliente</div>
          </div>

          {/* MAPA DE UBICACIÓN */}
          {(property.latitude && property.longitude && mapsApiKey) && (
            <>
              <hr style={divider} />
              <h3 style={smallTitle}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={18} color="#003366" /> Dónde estarás
                </span>
              </h3>
              <p style={{ fontSize: '14px', color: '#717171', marginBottom: '12px' }}>
                {property.address || 'Cartagena, Colombia'}
              </p>
              <PropertyMap
                lat={property.latitude}
                lng={property.longitude}
                address={property.address}
                apiKey={mapsApiKey}
              />
              <p style={{ fontSize: '12px', color: '#aaa', marginTop: '8px' }}>
                La ubicación exacta se comparte después de confirmar la reserva.
              </p>
            </>
          )}
        </div>

        {/* COLUMNA DERECHA: TARJETA DE RESERVA (STICKY) */}
        <div style={bookingColumn} className="property-booking-column">
          <div style={stickyWrapper} className="property-sticky-wrapper">
            <PropertyBookingCard property={property} auth={auth} settings={settings} />
            {/* ELIMINADO: Link de denunciar anuncio */}
          </div>
        </div>

      </div>
    </div>
  );
};

// --- ESTILOS REFINADOS (Mantenidos y corregidos) ---
const pageLayout = { maxWidth: '1120px', margin: '0 auto', padding: '0 40px', textAlign: 'left' };
const loaderContainer = { height: '80vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' };

const headerSection = { padding: '24px 0' };
const mainTitle = { fontSize: '28px', fontWeight: '800', marginBottom: '8px', color: '#003366' };
const subHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };

const infoGroup = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' };
const ratingBadge = { display: 'flex', alignItems: 'center', gap: '4px' };
const dotDivider = { color: '#717171' };
const locationText = { textDecoration: 'underline', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' };

const shareActions = { display: 'flex', gap: '15px' };
const textActionBtn = { background: 'none', border: 'none', textDecoration: 'underline', fontWeight: '600', cursor: 'pointer', fontSize: '14px' };

const mainContentGrid = { display: 'grid', gridTemplateColumns: '1fr 380px', gap: '80px', marginTop: '48px' };
const infoColumn = { paddingBottom: '100px', minWidth: 0 };

const hostSection = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' };
const specList = { fontSize: '16px', color: '#444' };

const divider = { border: 'none', borderTop: '1px solid #eee', margin: '32px 0' };

const highlightSection = { display: 'flex', flexDirection: 'column', gap: '24px' };
const highlightRow = { display: 'flex', gap: '16px', alignItems: 'flex-start' };
const highlightTitle = { fontWeight: '700', fontSize: '16px', color: '#222' };
const highlightSub = { color: '#717171', fontSize: '14px', lineHeight: '1.4' };

const descriptionSection = { padding: '8px 0' };
const smallTitle = { fontSize: '18px', fontWeight: '800', marginBottom: '24px', color: '#222' };
const descriptionText = { fontSize: '16px', lineHeight: '26px', color: '#444', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 };

const roomsToggleBtn = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: '1px solid #eee', borderRadius: '14px', padding: '16px 20px', cursor: 'pointer', fontSize: '16px', fontWeight: '800', color: '#222' };
const roomsScrollRow = { display: 'flex', gap: '16px', overflowX: 'auto', padding: '20px 4px', scrollSnapType: 'x mandatory' };
const roomCard = { flex: '0 0 220px', scrollSnapAlign: 'start', background: '#f8fafc', border: '1px solid #eee', borderRadius: '16px', padding: '18px' };
const roomCardTitle = { fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#003366', marginBottom: '12px' };
const roomCardRow = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#444', marginBottom: '6px' };
const roomChipsRow = { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' };
const roomChip = { fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '999px', background: '#e0ecff', color: '#003366' };

const amenitiesGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' };
const amenityItem = { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '16px', color: '#444' };

const bookingColumn = { position: 'relative' };
const stickyWrapper = { position: 'sticky', top: '100px' };

export default PropertyDetailView;